// ABOUTME: Loads the spark-ui chrome on demand, keeping spark-ui and its Kendo dependencies out of the
// ABOUTME: main bundle, and keeps a failed load from taking the page with it.

import * as Sentry from "@sentry/react";
import ConfirmModal from "components/common/ConfirmModal";
import Loading from "components/common/Loading";
import React, { lazy, Suspense } from "react";
import { SPARK_ISOLATE_CLASS } from "./sparkIsolate";

// PackManager names its own attempt the same, so a search for either app finds both.
const RELOAD_ATTEMPT_KEY = "spark_nav.reloaded_for_missing_chunk";
const REPORT_FLUSH_MS = 2000;

const SparkApplicationLayoutView = lazy(loadSparkChrome);

export default function SparkApplicationLayout({ children }) {
  return (
    <Suspense fallback={<Loading />}>
      <SparkApplicationLayoutView>{children}</SparkApplicationLayoutView>
    </Suspense>
  );
}

function loadSparkChrome() {
  return import("./SparkApplicationLayoutView").then((module) => {
    clearReloadAttempt();

    return module;
  }, chromeFailed);
}

function chromeFailed(error) {
  const outcome = claimReloadAttempt();

  Sentry.captureException(error, { tags: { sparkChrome: outcome } });

  if (outcome === "reloading") {
    // Vite marks a stylesheet seen before it settles, so retrying in this document can never re-fetch it.
    const reload = () => window.location.reload();

    // Navigating would cut the report off mid-flight.
    Sentry.flush(REPORT_FLUSH_MS).then(reload, reload);

    return { default: Loading };
  }

  // Already in the main bundle, so it cannot fail the way the chrome just did.
  return { default: PageWithoutChrome };
}

function PageWithoutChrome({ children }) {
  return (
    <div className={`ps-app-content ${SPARK_ISOLATE_CLASS}`}>
      {children}
      <ConfirmModal />
    </div>
  );
}

function claimReloadAttempt() {
  // Offline, a reload lands on the browser's own error page and takes the app with it.
  if (navigator.onLine === false) return "offline";

  try {
    if (window.sessionStorage.getItem(RELOAD_ATTEMPT_KEY)) return "already-reloaded";

    window.sessionStorage.setItem(RELOAD_ATTEMPT_KEY, "1");
  } catch {
    return "storage-blocked";
  }

  return "reloading";
}

// Lets a tab that recovered spend a reload again on a later deploy.
function clearReloadAttempt() {
  try {
    window.sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
  } catch {}
}
